import { Descriptions, Space, Tag, Typography } from 'antd';
import { formatDateTime, useNarrowViewport } from '@viq-admin/ui';
import type { User } from '@viq-admin/types';
import { CompanyLink, UserLink } from '../../components/EntityLinks';
import { useAllUsers } from '../../lookups/useLookups';
import { RoleTag } from './RoleTag';
import { When } from './When';

/**
 * The user's summary facts, the first thing under the header: status, last
 * login (exact time and how long ago), created, company and role. All of it
 * comes from the user record the page already loaded (GET
 * /v1/superadmin/users/{oid}), so this section has no loading state of its
 * own beyond the page's.
 *
 * Last login was only on the users list before; "when did she last sign in"
 * is usually the first question about a user, so it is here too.
 *
 * Three columns on desktop, one on a phone.
 */
export function UserFacts({
  user,
}: {
  user: User & { companyName: string | null };
}) {
  const narrow = useNarrowViewport();
  return (
    <Descriptions
      size="small"
      column={narrow ? 1 : 3}
      items={[
        { key: 'status', label: 'Status', children: <StatusFact user={user} /> },
        {
          key: 'lastLogin',
          label: 'Last login',
          children: <When iso={user.lastLoginAt} empty="Never" />,
        },
        { key: 'created', label: 'Created', children: formatDateTime(user.createdAt) },
        {
          key: 'company',
          label: 'Company',
          children: <CompanyLink id={user.companyId} name={user.companyName} />,
        },
        { key: 'role', label: 'Role', children: <RoleTag role={user.role} /> },
      ]}
    />
  );
}

/**
 * Active, or Deactivated with when, by whom and why. The deactivation columns
 * are written by both planes' deactivate handlers: `deactivatedBy` is the
 * acting admin's oid.
 */
function StatusFact({ user }: { user: User }) {
  if (!user.deactivatedAt) return <Tag color="success">Active</Tag>;
  return (
    <Space direction="vertical" size={2}>
      <span>
        <Tag>Deactivated</Tag>
        {formatDateTime(user.deactivatedAt)}
        {user.deactivatedBy ? <DeactivatedBy oid={user.deactivatedBy} /> : null}
      </span>
      {user.deactivationReason ? (
        <Typography.Text type="secondary">Reason: {user.deactivationReason}</Typography.Text>
      ) : null}
    </Space>
  );
}

/**
 * Who deactivated the user. A company admin who did it from the portal is a
 * user like any other, so they resolve through the cross-tenant users lookup
 * and link to their own page. A super admin who did it from the console is a
 * workforce-tenant account with no user record, so an oid that does not
 * resolve is shown as "a super admin", with the oid on hover for anyone who
 * needs to trace it. Nothing renders until the lookup has loaded, so the
 * label never flips from one to the other, and a failed lookup just leaves
 * the "by" part off.
 */
function DeactivatedBy({ oid }: { oid: string }) {
  const { users, isLoading, error } = useAllUsers();
  if (isLoading || error) return null;
  const match = users.find((u) => u.oid === oid);
  return (
    <>
      {' by '}
      {match ? (
        <UserLink oid={match.oid} name={match.name || match.email} />
      ) : (
        <span title={oid}>a super admin</span>
      )}
    </>
  );
}
